import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import SearchUrlContainer from '../../../components/searchUrl/containers/SearchUrlContainer';
import I18n from '../../../config/i18n';
import { Grid, Row, Col } from '../../../utils/grid/Grid';
import Toggle from '../../../utils/Toggle';
import { MapContainer } from '../../../utils/googleMaps/MapContainer';
import Pagination from '../../../components/pagination/Pagination';
import SearchWidgetContainer from '../../../components/search/components/SearchWidgetContainer';
import SearchWidgetBigContainer from '../../../components/search/containers/SearchWidgetBigContainer';
import SearchFilterContainer from '../../../components/searchFilter/containers/SearchFilterContainer';
import HostTeaserSearchList from '../../../components/hostTeaser/HostTeaserSearchList';
import Meta from '../../../utils/meta/Meta';
import ToggleSection from '../../../utils/toggleSection/ToggleSection';

import './search.css';
import { RequestState, FilterSet } from '../../../features/request/types';
import FilterReset from './FilterReset';

interface SearchProps {
  hosts: {
    country: string;
    accommodation: {
      slug: string;
      id: number;
      kind: string;
      max_persons: number;
    };
    image: string;
    images: {
      id: number;
      url: string;
    }[];
    display_title: string;
    locality: string;
    selling_line: string;
    price_per_night: number;
    region?: string;
    linkTarget?: string;
    showPrices?: boolean;
  }[];
  className?: string;
  totalPages: number;
  onPageChange: (index: number) => void;
  loading: boolean;
  lang: string;
  request: RequestState;
  fetchCountryOptions: () => void;
  fetchRegionOptions: (country: string) => void;
  fetchCityOptions: (country: string, region: string) => void;
  resetFilterSet: () => void;
}

interface WindowHeights {
  scrollTop: number;
  footerHeight: number;
  headerHeight: number;
  windowHeight: number;
  innerHeight: number;
  searchBarHeight: number;
}

interface StickyStyle {
  height?: number;
  maxHeight?: number;
  top?: number;
}

function getScrollTop(): number {
  return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
}

export default function Search({
  loading,
  hosts,
  totalPages,
  request,
  lang,
  onPageChange,
  fetchCountryOptions,
  fetchRegionOptions,
  fetchCityOptions,
  resetFilterSet,
}: SearchProps): JSX.Element {
  const [windowHeights, setWindowHeights] = useState<WindowHeights>({
    scrollTop: 0,
    footerHeight: 0,
    headerHeight: 0,
    windowHeight: 0,
    innerHeight: 0,
    searchBarHeight: 0,
  });
  const [sticky, setSticky] = useState(true);
  const [stickyStyle, setStickyStyle] = useState<StickyStyle>({});
  const [stickyBottom, setStickyBottom] = useState(false);
  // const el = React.createRef<HTMLDivElement>();
  const el = useRef<HTMLDivElement>(null);

  function handleScroll(): void {
    const footerElement = document.getElementById('content-footer');
    const headerElement = document.getElementById('content-header');

    const scrollTop = getScrollTop();
    const footerHeight = footerElement ? footerElement.offsetHeight : 0;
    const headerHeight = headerElement ? headerElement.offsetHeight : 0;
    const windowHeight = window.innerHeight;
    const innerHeight = document.body.offsetHeight;
    const searchBarHeight = el.current ? el.current.offsetHeight : 0;
    setWindowHeights({
      scrollTop,
      footerHeight,
      headerHeight,
      windowHeight,
      innerHeight,
      searchBarHeight,
    });
  }

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function calculateStickyStyle(): void {
    let newStickyStyle: StickyStyle = {};
    if (stickyBottom) {
      newStickyStyle = { height: windowHeights.windowHeight - windowHeights.headerHeight };
    } else if (sticky) {
      newStickyStyle = { top: windowHeights.headerHeight };
    } else {
      newStickyStyle = { maxHeight: windowHeights.windowHeight - windowHeights.headerHeight };
    }
    if (
      newStickyStyle.height !== stickyStyle.height ||
      newStickyStyle.maxHeight !== stickyStyle.maxHeight ||
      newStickyStyle.top !== stickyStyle.top
    ) {
      setStickyStyle(newStickyStyle);
    }
  }

  function calculateStickyness(): void {
    const { scrollTop, footerHeight, windowHeight, innerHeight, searchBarHeight } = windowHeights;
    const footerVisiblePos = innerHeight - footerHeight - windowHeight;
    if (scrollTop <= searchBarHeight) {
      if (sticky) {
        setSticky(false);
      }
      if (stickyBottom) {
        setStickyBottom(false);
      }
    } else if (scrollTop > searchBarHeight && scrollTop < footerVisiblePos) {
      if (!sticky) {
        setSticky(true);
      }
      if (stickyBottom) {
        setStickyBottom(false);
      }
    } else if (scrollTop > footerVisiblePos) {
      if (sticky) {
        setSticky(false);
      }
      if (!stickyBottom) {
        setStickyBottom(true);
      }
    }
  }

  useEffect(() => {
    calculateStickyness();
    calculateStickyStyle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowHeights]);

  useEffect(() => {
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hosts.length]);

  const { countryCode, region } = request;

  useEffect(() => {
    fetchCountryOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (!countryCode) {
      return;
    }
    fetchRegionOptions(countryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, countryCode]);

  useEffect(() => {
    if (!countryCode || !region) {
      return;
    }
    fetchCityOptions(countryCode, region);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, countryCode, region]);

  function handlePageClick(data: any): void {
    const pageIndex = data.selected;
    onPageChange(pageIndex);
  }

  return useMemo(
    () => (
      <Grid fluid>
        <Meta />
        <Row>
          <Col xs={12} className="search-bar">
            <ToggleSection
              label={I18n.t('search.adjust_request')}
              className="search-toggle-section search-toggle-section-search"
            >
              <SearchWidgetContainer ref={el}>
                <SearchWidgetBigContainer options={{}} withButton={false} />
              </SearchWidgetContainer>
            </ToggleSection>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <ToggleSection
              label={I18n.t('search.adjust_filters')}
              className="search-toggle-section search-toggle-section-filter"
            >
              <SearchFilterContainer />
            </ToggleSection>
            <FilterReset filterSet={request.filterSet} resetFilterSet={resetFilterSet} />
            <HostTeaserSearchList
              hosts={hosts as any}
              className={classNames('search-results', { loading })}
            />
            {hosts.length > 1 && (
              <FilterReset filterSet={request.filterSet} resetFilterSet={resetFilterSet} />
            )}
            <Pagination
              onPageChange={(data: any) => handlePageClick(data)}
              pageCount={totalPages}
              selectedPage={request.page}
            />
          </Col>
          <Col md={6} className="search-map-origin">
            <Toggle className="search-map-toggle-container" hide="xs sm">
              <div
                className={classNames('search-map-container', {
                  sticky,
                  'sticky-bottom': stickyBottom,
                })}
                style={stickyStyle}
              >
                <MapContainer markers={hosts} zoom={6} />
              </div>
            </Toggle>
          </Col>
        </Row>
        <SearchUrlContainer />
      </Grid>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sticky, stickyBottom, stickyStyle, loading, request, hosts, totalPages],
  );
}
